'use client';

import React, { useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, Bookmark, X, ShoppingBag } from 'lucide-react';
import type { SavedOutfit } from '@/lib/services/savedOutfitsService';
import { buildGoogleShoppingUrl } from '@/app/fashion-discovery-interface/lib/gemini';

interface SavedOutfitsCarouselProps {
  savedOutfits: SavedOutfit[];
  onRemove: (id: string) => void;
  onViewOutfit: (saved: SavedOutfit) => void;
}

export default function SavedOutfitsCarousel({
  savedOutfits,
  onRemove,
  onViewOutfit,
}: SavedOutfitsCarouselProps) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  if (savedOutfits.length === 0) return null;

  const updateScrollState = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 8);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 8);
  };

  const scroll = (dir: 'left' | 'right') => {
    const el = scrollRef.current;
    if (!el) return;
    el.scrollBy({ left: dir === 'left' ? -280 : 280, behavior: 'smooth' });
    setTimeout(updateScrollState, 350);
  };

  return (
    <div
      className="w-full py-5 sm:py-6"
      style={{ backgroundColor: '#FAF7F2', borderBottom: '1px solid hsl(35 20% 88%)' }}
    >
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-10 2xl:px-16">
        {/* Header row */}
        <div className="flex items-center justify-between mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <div
              className="w-6 h-6 rounded-full flex items-center justify-center"
              style={{ backgroundColor: 'rgba(201,169,110,0.15)' }}
            >
              <Bookmark size={12} style={{ color: '#C9A96E' }} />
            </div>
            <span className="text-sm font-semibold" style={{ color: '#1A1A1A' }}>
              Saved Looks
            </span>
            <span
              className="text-xs px-2 py-0.5 rounded-full font-medium"
              style={{ backgroundColor: 'rgba(201,169,110,0.15)', color: '#C9A96E' }}
            >
              {savedOutfits.length}
            </span>
          </div>
          {/* Scroll arrows */}
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => scroll('left')}
              disabled={!canScrollLeft}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 disabled:opacity-30"
              style={{ backgroundColor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
              aria-label="Scroll left"
            >
              <ChevronLeft size={14} style={{ color: '#1A1A1A' }} />
            </button>
            <button
              onClick={() => scroll('right')}
              disabled={!canScrollRight}
              className="w-7 h-7 rounded-full flex items-center justify-center transition-all duration-150 disabled:opacity-30"
              style={{ backgroundColor: 'white', boxShadow: '0 1px 4px rgba(0,0,0,0.1)' }}
              aria-label="Scroll right"
            >
              <ChevronRight size={14} style={{ color: '#1A1A1A' }} />
            </button>
          </div>
        </div>

        {/* Scrollable carousel */}
        <div
          ref={scrollRef}
          onScroll={updateScrollState}
          className="flex gap-3 overflow-x-auto pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {savedOutfits.map((saved) => (
            <CarouselCard
              key={saved.id}
              saved={saved}
              onRemove={onRemove}
              onView={onViewOutfit}
            />
          ))}
        </div>
      </div>
    </div>
  );
}

function CarouselCard({
  saved,
  onRemove,
  onView,
}: {
  saved: SavedOutfit;
  onRemove: (id: string) => void;
  onView: (saved: SavedOutfit) => void;
}) {
  const [hovered, setHovered] = useState(false);
  const currencySymbol =
    saved.wizardData.currency === 'EUR' ?'€'
      : saved.wizardData.currency === 'GBP' ?'£'
      : saved.wizardData.currency === 'CAD' ?'C$'
      : saved.wizardData.currency === 'AUD' ?'A$' :'$';

  const totalEstimate = saved.outfit.items.reduce((sum, item) => {
    const match = item.estimatedPrice.match(/[\d,]+(\.\d+)?/);
    return sum + (match ? parseFloat(match[0].replace(',', '')) : 0);
  }, 0);

  const firstItem = saved.outfit.items[0];
  const shopUrl = firstItem
    ? buildGoogleShoppingUrl(firstItem.name, firstItem.category)
    : '#';

  return (
    <div
      className="relative shrink-0 rounded-2xl overflow-hidden cursor-pointer transition-all duration-200"
      style={{
        width: '220px',
        backgroundColor: 'white',
        boxShadow: hovered
          ? '0 8px 24px rgba(0,0,0,0.12)'
          : '0 2px 8px rgba(0,0,0,0.06)',
        transform: hovered ? 'translateY(-3px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onView(saved)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onView(saved)}
      aria-label={`View saved outfit: ${saved.outfit.editorialTitle}`}
    >
      {/* Gold accent bar */}
      <div className="h-1" style={{ backgroundColor: '#C9A96E' }} />

      <div className="p-3.5">
        {/* Title */}
        <p
          className="text-xs font-semibold leading-snug mb-1 line-clamp-2"
          style={{ color: '#1A1A1A', fontFamily: 'var(--font-serif-display, Georgia, serif)' }}
        >
          {saved.outfit.editorialTitle}
        </p>
        <p className="text-xs mb-2.5 line-clamp-1" style={{ color: 'hsl(30 8% 52%)' }}>
          {saved.wizardData.occasion}
        </p>

        {/* Item count + aesthetic chips */}
        <div className="flex flex-wrap gap-1 mb-3">
          <span
            className="text-xs px-1.5 py-0.5 rounded-full"
            style={{ backgroundColor: 'hsl(35 15% 92%)', color: 'hsl(30 8% 52%)' }}
          >
            {saved.outfit.items.length} pieces
          </span>
          <span
            className="text-xs px-1.5 py-0.5 rounded-full truncate max-w-[90px]"
            style={{ backgroundColor: 'rgba(201,169,110,0.12)', color: '#C9A96E' }}
          >
            {saved.outfit.aesthetic}
          </span>
        </div>

        {/* Price + shop link */}
        <div className="flex items-center justify-between pt-2.5 border-t" style={{ borderColor: 'hsl(35 15% 92%)' }}>
          <div>
            <p className="text-xs" style={{ color: 'hsl(30 8% 52%)' }}>Est. total</p>
            <p className="text-sm font-bold tabular-nums" style={{ color: '#1A1A1A' }}>
              {currencySymbol}
              {totalEstimate.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
            </p>
          </div>
          <a
            href={shopUrl}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium transition-all duration-150 hover:opacity-80"
            style={{ backgroundColor: '#1A1A1A', color: 'white' }}
            aria-label="Shop first item"
          >
            <ShoppingBag size={10} />
            Shop
          </a>
        </div>
      </div>

      {/* Remove button */}
      <button
        onClick={(e) => {
          e.stopPropagation();
          onRemove(saved.id);
        }}
        className="absolute top-2.5 right-2.5 w-5 h-5 rounded-full flex items-center justify-center transition-all duration-150 hover:opacity-100 opacity-0 group-hover:opacity-100"
        style={{
          backgroundColor: 'rgba(0,0,0,0.5)',
          color: 'white',
          opacity: hovered ? 1 : 0,
        }}
        aria-label="Remove saved outfit"
        title="Remove from saved"
      >
        <X size={10} />
      </button>
    </div>
  );
}
